import { useEffect } from 'react'
import { IFacilityContact, ILocation, Maybe } from 'types/graphqlTypes'
import { IFacilityHourFragment } from 'apps/marketplace/graphql/FacilityHourFragment'
import { useShipperFacilityLazyQuery } from 'apps/marketplace/graphql/ShipperFacility'

export interface IUseShipperFacilityState {
  loading: boolean
  facilityCodes: string
  facilityName: string
  facilityUuid: string
  facilityLocation: Maybe<
    Pick<ILocation, 'id' | 'address' | 'addressLine2' | 'city' | 'stateCode' | 'zipcode'>
  >
  scheduler: Maybe<
    Pick<IFacilityContact, 'id' | 'name' | 'contactType' | 'email' | 'phone' | 'phoneExtension'>
  >
  scheduleHours: Maybe<IFacilityHourFragment[]>
  schedulingNotes: Maybe<string>
}

export interface IUseShipperFacilityProps {
  shipperFacilityUuid: string | undefined
}

const useShipperFacility = ({
  shipperFacilityUuid,
}: IUseShipperFacilityProps): IUseShipperFacilityState => {
  const [fetchShipperFacility, { data, loading }] = useShipperFacilityLazyQuery()

  // Facility
  const { mergedFacilities } = data || {}
  const [mergedFacility] = mergedFacilities || []
  const { facility, shipperFacility } = mergedFacility || {}
  const { location, name, contacts = [], schedule, uuid, scheduleHours } = facility || {}

  // Shipper Facility Name
  const { identifiers = [] } = shipperFacility || {}
  const facilityCodes = identifiers.map(({ facilityCode }) => facilityCode).join(' · ')

  // Scheduling Notes
  const { schedulingNotes } = schedule || {}

  // Facility Scheduler
  const schedulerContact = contacts.filter(({ contactType }) => contactType === 'scheduler')
  const [scheduler] = schedulerContact || []

  /**
   * Fetch Shipper Facility data when uuid is provided
   */
  useEffect(() => {
    if (shipperFacilityUuid) {
      fetchShipperFacility({
        variables: {
          shipperFacilityUuid,
        },
      })
    }
  }, [shipperFacilityUuid, fetchShipperFacility])

  return {
    loading,
    facilityCodes,
    facilityName: name || '',
    facilityLocation: location,
    facilityUuid: uuid || '',
    scheduler,
    scheduleHours,
    schedulingNotes,
  }
}

export default useShipperFacility
